/**
 * The chamfer (Fase) in the outline draft — the rules, the arithmetic, the
 * numbering.
 *
 * A chamfer cuts one corner of the shape off with a slant. What is **stored**
 * is two whole-millimetre legs, measured from the imagined corner point along
 * the two walls — the way the customer measured them (`DraftChamfer`). Angle
 * and slant length are only ever *entered* or *shown*; they are derived here
 * from the legs and never kept (design 2026-10-18).
 *
 * Pure millimetres, no React, no screen coordinates — like `draft.ts`, which
 * it serves: the gestures there ask `chamferProblem` which chamfers survive.
 *
 * **One wording with the server.** `chamferProblem` mirrors
 * `document.chamfer_problem` (apps/api) case by case and sentence by
 * sentence — a person who is told "no" in the editor and again by the server
 * hears the same words. The editor has no corner marks (mitre, fill are set
 * on the board), so that last server case never occurs here.
 */

import type { Draft, DraftChamfer } from "./draft";
import { formatMm } from "./format";

export type { DraftChamfer } from "./draft";

// ── Legs, slant, angle ─────────────────────────────────────────────────────

/** The angle field takes 1° to 89°: 0° and 90° are no slant at all. */
const MIN_ANGLE_DEG = 1;
const MAX_ANGLE_DEG = 89;

const RAD_PER_DEG = Math.PI / 180;

/**
 * The two legs from an angle and **one** other figure — a leg, or the slant's
 * length. `angleDeg` is the angle the slant makes with leg A (the wall the
 * chamfer ends): `tan α = b / a`.
 *
 * - leg A given: `b = round(a · tan α)`;
 * - slant given: `a = round(d · cos α)`, `b = round(d · sin α)`.
 *
 * Whole millimetres, `Math.round`. A very flat angle on a short leg can round
 * a leg to 0 — that is `chamferProblem`'s to refuse, not this function's.
 * An angle outside 1–89 throws `RangeError`; the field that feeds this keeps
 * itself in range, so reaching it is a bug, not a typo.
 */
export function legsFromAngle(
  angleDeg: number,
  given: { legAMm: number } | { diagonalMm: number },
): { legAMm: number; legBMm: number } {
  if (!(angleDeg >= MIN_ANGLE_DEG && angleDeg <= MAX_ANGLE_DEG)) {
    throw new RangeError(`Der Winkel liegt zwischen ${MIN_ANGLE_DEG}° und ${MAX_ANGLE_DEG}°, nicht bei ${angleDeg}°.`);
  }
  const alpha = angleDeg * RAD_PER_DEG;
  if ("legAMm" in given) {
    return { legAMm: given.legAMm, legBMm: Math.round(given.legAMm * Math.tan(alpha)) };
  }
  return {
    legAMm: Math.round(given.diagonalMm * Math.cos(alpha)),
    legBMm: Math.round(given.diagonalMm * Math.sin(alpha)),
  };
}

/** The slant's length: the legs' hypotenuse, whole millimetres (the server's `diagonal_mm`). */
export function diagonalMm(legAMm: number, legBMm: number): number {
  return Math.round(Math.hypot(legAMm, legBMm));
}

/** The slant's angle against leg A, degrees to one decimal — 45 for equal legs. */
export function angleOf(legAMm: number, legBMm: number): number {
  return Math.round(Math.atan2(legBMm, legAMm) * (10 / RAD_PER_DEG)) / 10;
}

// ── What may stand ─────────────────────────────────────────────────────────

/** The wall after `wall`, wrapping the seam of a ring; `null` at the end of an open chain. */
function wallAfter(draft: Draft, wall: number): number | null {
  const count = draft.walls.length;
  if (draft.closed) return (wall + 1) % count;
  return wall + 1 < count ? wall + 1 : null;
}

/** The wall before `wall`, wrapping the seam of a ring; `null` before the first wall of an open chain. */
function wallBefore(draft: Draft, wall: number): number | null {
  const count = draft.walls.length;
  if (draft.closed) return (wall - 1 + count) % count;
  return wall > 0 ? wall - 1 : null;
}

/** Whether two walls meet at a right angle — the only corner a chamfer can cut. */
function meetAtRightAngle(draft: Draft, first: number, second: number): boolean {
  const step = (wall: number) => {
    const from = draft.vertices[wall]!;
    const to = draft.vertices[wall + 1]!;
    return { dx: to.xMm - from.xMm, dy: to.yMm - from.yMm };
  };
  const a = step(first);
  const b = step(second);
  const moving = (s: { dx: number; dy: number }) => s.dx !== 0 || s.dy !== 0;
  return moving(a) && moving(b) && a.dx * b.dx + a.dy * b.dy === 0;
}

/**
 * Why `chamfer` may not stand in `draft` — a German sentence, or `null`.
 *
 * `draft` is the draft **with all its chamfers**; `chamfer` is among them or,
 * if it is not, is checked as though it replaced the entry on its corner —
 * whether one chamfer has room depends on the other one eating the same wall
 * from its other end.
 *
 * The conditions, in the server's order:
 *
 * - the wall exists, and its end is a corner — not the open end of a chain;
 * - the two walls meet at a right angle: the same direction and the reverse
 *   are both a line, not a corner;
 * - at most one chamfer per corner;
 * - **both walls carry a figure** (`lengthMm`, typed or read — A-014): every
 *   length the slant and the shortened walls get is computed from the legs and
 *   these figures, so one nobody stated would be a production dimension
 *   invented from the drawing;
 * - each leg is a whole number of at least 1 mm;
 * - on each of the two walls a stretch **greater than zero** is left after
 *   every leg lying on it is taken off — the chamfer's own, and the one a
 *   chamfer at the wall's other end puts there. (Whether that stretch is long
 *   enough to be laid is the board's rule about remainders, not this one.)
 *
 * The sentences name no wall number, for the reason the server's do not: the
 * person is looking at the corner.
 */
export function chamferProblem(draft: Draft, chamfer: DraftChamfer): string | null {
  const count = draft.walls.length;
  const index = chamfer.wallIndex;
  if (!Number.isInteger(index) || index < 0 || index >= count) {
    return (
      `Die Fase sitzt an Wand ${index + 1}, die Form hat aber nur ` +
      `${count === 1 ? "eine Wand" : `${count} Wände`}.`
    );
  }
  const next = wallAfter(draft, index);
  if (next === null) {
    return "Am offenen Ende einer Wandkette ist keine Ecke — dort lässt sich keine Fase setzen.";
  }
  if (!meetAtRightAngle(draft, index, next)) {
    return (
      "Die beiden Wände laufen in einer Linie — das ist eine Gerade " +
      "und keine Ecke, die sich abschrägen ließe."
    );
  }
  const chamfers = draft.chamfers ?? [];
  if (chamfers.filter((each) => each.wallIndex === index).length > 1) {
    return "An einer Ecke sitzt höchstens eine Fase.";
  }
  if (draft.walls[index]!.lengthMm === null || draft.walls[next]!.lengthMm === null) {
    return "Eine Fase braucht an beiden Wänden der Ecke ein Maß — erst die Maße eintragen.";
  }
  if (!Number.isInteger(chamfer.legAMm) || !Number.isInteger(chamfer.legBMm)) {
    return "Die Schenkel einer Fase sind ganze Millimeter.";
  }
  if (chamfer.legAMm < 1 || chamfer.legBMm < 1) {
    return "Die Schenkel einer Fase sind mindestens 1 mm lang.";
  }

  const byWall = new Map(chamfers.map((each) => [each.wallIndex, each]));
  byWall.set(index, chamfer);
  for (const wall of [index, next]) {
    const before = wallBefore(draft, wall);
    const taken =
      (byWall.get(wall)?.legAMm ?? 0) + (before === null ? 0 : (byWall.get(before)?.legBMm ?? 0));
    const measure = draft.walls[wall]!.lengthMm!;
    if (measure - taken <= 0) {
      return (
        `Die Fase passt nicht: Von ${formatMm(measure)} Wandmaß bliebe ` +
        "nach Abzug aller Schenkel keine belegbare Strecke."
      );
    }
  }
  return null;
}

// ── Setting and removing ───────────────────────────────────────────────────

/**
 * `draft` with `chamfer` on its corner, replacing the one already there. The
 * list stays in wall order, so two drafts with the same chamfers are equal
 * whichever was set first.
 *
 * Returns the draft untouched where `chamferProblem` says the chamfer cannot
 * stand, like every gesture in `draft.ts` — the screen asks `chamferProblem`
 * first to have the sentence to show.
 */
export function setChamfer(draft: Draft, chamfer: DraftChamfer): Draft {
  const others = (draft.chamfers ?? []).filter((each) => each.wallIndex !== chamfer.wallIndex);
  const candidate: Draft = {
    ...draft,
    chamfers: [...others, chamfer].sort((a, b) => a.wallIndex - b.wallIndex),
  };
  return chamferProblem(candidate, chamfer) === null ? candidate : draft;
}

/**
 * `draft` without the chamfer on the end corner of `wallIndex`; the list goes
 * with the last one, so a draft with none is the draft it was before
 * chamfers. Untouched where there is none to remove.
 */
export function removeChamfer(draft: Draft, wallIndex: number): Draft {
  const kept = (draft.chamfers ?? []).filter((each) => each.wallIndex !== wallIndex);
  if (kept.length === (draft.chamfers ?? []).length) return draft;
  const rest: Draft = { ...draft };
  if (kept.length > 0) rest.chamfers = kept;
  else delete rest.chamfers;
  return rest;
}

/**
 * `draft` without the chamfers that no longer stand on it, and each one taken
 * off with the sentence `chamferProblem` gives for it — for the screen, which
 * has to **say** that a chamfer went (design 2026-10-18 §8: „sichtbar und mit
 * Grund, nie still“).
 *
 * For a typed figure, which leaves every corner where it is and changes only
 * what a wall measures: a cleared figure strands the chamfers at both its
 * ends, a shorter one may leave no room for the legs on it. One at a time, in
 * wall order, each re-checked without the ones already taken — two chamfers
 * eating one wall from both ends may not both fit, and dropping the first can
 * leave room for the second.
 *
 * The very same draft where every chamfer still stands; the `chamfers` key
 * goes with the last one, as in `removeChamfer`.
 */
export function dropStrandedChamfers(draft: Draft): {
  draft: Draft;
  dropped: { chamfer: DraftChamfer; reason: string }[];
} {
  const dropped: { chamfer: DraftChamfer; reason: string }[] = [];
  let kept = draft.chamfers ?? [];
  for (;;) {
    const trial: Draft = { ...draft, chamfers: kept };
    let failing: { chamfer: DraftChamfer; reason: string } | null = null;
    for (const chamfer of kept) {
      const reason = chamferProblem(trial, chamfer);
      if (reason !== null) {
        failing = { chamfer, reason };
        break;
      }
    }
    if (failing === null) break;
    dropped.push(failing);
    const gone = failing.chamfer;
    kept = kept.filter((chamfer) => chamfer !== gone);
  }
  if (dropped.length === 0) return { draft, dropped };
  const rest: Draft = { ...draft };
  if (kept.length > 0) rest.chamfers = kept;
  else delete rest.chamfers;
  return { draft: rest, dropped };
}

// ── Numbers and measures for display ───────────────────────────────────────

/**
 * The number the person sees on wall `wallIndex`: counted from 1, and **the
 * slant counts as a wall of its own**, right after the wall whose end corner
 * it cuts — so every wall after a chamfer moves on by one.
 *
 * The chamfer on the seam (the last wall of a ring) shifts nothing: its slant
 * is the last number, after every wall. That is the order the server expands
 * the ring in, and the numbers on the board and in the parts list.
 */
export function displayWallNumber(draft: Draft, wallIndex: number): number {
  const before = (draft.chamfers ?? []).filter((chamfer) => chamfer.wallIndex < wallIndex).length;
  return wallIndex + 1 + before;
}

/** The number of the slant after wall `wallIndex` — the next one after that wall's own. */
export function chamferWallNumber(draft: Draft, wallIndex: number): number {
  return displayWallNumber(draft, wallIndex) + 1;
}

/**
 * What of wall `wallIndex` the gratings can lie on: the customer's measure up
 * to the imagined corner point, less the leg of the chamfer at its end and
 * the leg of the chamfer at its start (the one keyed by the wall before — for
 * the first wall of a ring, the last wall's). `null` where nobody has stated
 * the wall's length.
 */
export function usableMm(draft: Draft, wallIndex: number): number | null {
  const measure = draft.walls[wallIndex]?.lengthMm ?? null;
  if (measure === null) return null;
  const chamfers = draft.chamfers ?? [];
  const before = wallBefore(draft, wallIndex);
  const atEnd = chamfers.find((chamfer) => chamfer.wallIndex === wallIndex)?.legAMm ?? 0;
  const atStart =
    before === null ? 0 : (chamfers.find((chamfer) => chamfer.wallIndex === before)?.legBMm ?? 0);
  return measure - atEnd - atStart;
}
